import React from "react";
import { useTranslation } from "react-i18next";
import { StyledH2 } from "@aureskonnect/react-ui";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";
import { useSnapshot } from "valtio";
import { useSelector } from "react-redux";
import classnames from "classnames";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";

import { store } from "../../../components/VerticalLayout/store";
import { capitalize } from "../../../helpers/general";
import { useAuthHeader } from "../../../api/headersKeycloak";
import { resetStore } from "../../Applications/DeviceAssociateModal/store";

import { RootState } from "../../../redux/store";

type HistoryModalProps = {
    isModalOpened: boolean;
    setIsModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
    setDataUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
};

export default function HistoryModal({
    isModalOpened,
    setIsModalOpened,
    setDataUpdated,
}: HistoryModalProps) {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const selectedFidelity = useSelector(
        (s: RootState) => s.fidelity.selectedFidelity
    );
    const { franchiseID, userID } = useSnapshot(store);

    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );
    const [, setArrayIds] = React.useState<string[]>([]);
    const [data, setData] = React.useState<any>({});
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);

    const ActionColumn = (e: { selectedRow: FidelityRow }) => {
        return e.selectedRow.subRows === undefined ? <div></div> : null;
    };
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";

    const url = `${
        process.env.REACT_APP_API_V2_URL
    }/settings/general/fidelity/history?userId=${userID}&franchiseId=${franchiseID}&fidelity=${selectedFidelity
        .map((el) => JSON.stringify(el))
        .join()}`;

    const ACTION_LABEL_MAP: Record<string, string> = {
        number_fidelity_points: t("Number of fidelity points"),
        money_correspondence: t("Money correspondence"),
        note: t("Remark"),
        password: t("Password"),
        authentication_mode: t("Authentication mode"),
        designation: t("Display name"),
        sortedVisibility: t("Changing Visibility"),
        Login: t("Login"),
        auresKonnectGroup: t("Group"),
        isCentralizationActive: t("State"),
        type: t("Loyalty"),
    };

    const arrayOfCustomColumns: customColumnProps[] = React.useMemo(
        () => [
            {
                indexOFColumn: 2,
                columnName: t("Actions"),
                customJsx: (e: { selectedRow: FidelityRow }) => {
                    const actionsArray = e.selectedRow.actions
                        .filter(
                            (el: {
                                item: string;
                                oldValue?: unknown;
                                newValue?: unknown;
                            }) =>
                                el.item !== "isActive" &&
                                ACTION_LABEL_MAP[el.item]
                        )
                        .map(
                            (el: { item: string }) => ACTION_LABEL_MAP[el.item]
                        );
                    if (e.selectedRow.operation === "modify") {
                        if (
                            e.selectedRow?.actions[0].item ===
                            "sortedVisibility"
                        ) {
                            return <span>{t("Changing Visibility")}</span>;
                        }

                        if (e.selectedRow?.actions[0].item === "isActive") {
                            return e.selectedRow?.actions[0].newValue ===
                                true ? (
                                <span>{t("Activation")}</span>
                            ) : (
                                <span>{t("Deactivation")}</span>
                            );
                        } else if (
                            e.selectedRow?.actions[0].item === "isArchived"
                        ) {
                            return e.selectedRow?.actions[0].newValue ===
                                true ? (
                                <span>{t("Archiving")}</span>
                            ) : (
                                <span>{t("Unarchiving")}</span>
                            );
                        }
                        return (
                            <span>
                                {t("Edit")}:{actionsArray.join(",")}
                            </span>
                        );
                    } else if (e.selectedRow.operation === "creation") {
                        return <span>{t("Creation")}</span>;
                    } else if (
                        e.selectedRow.operation.includes("reassignment")
                    ) {
                        return (
                            <span>
                                {t("Reassignment")}
                                {e.selectedRow.operation.substring(12)}
                            </span>
                        );
                    } else {
                        return <span>{t("Affectation")}</span>;
                    }
                },
            },
            {
                indexOFColumn: 3,
                columnName: t("Before edit"),
                customJsx: (e: { selectedRow: FidelityRow }) => {
                    if (e.selectedRow.operation === "modify") {
                        if (
                            e.selectedRow?.actions[0].item ===
                            "sortedVisibility"
                        ) {
                            return <span>{""}</span>;
                        } else {
                            return (
                                <span>
                                    {e.selectedRow?.actions[0]?.item ===
                                    "isArchived" ? (
                                        e.selectedRow?.actions[0]?.oldValue ===
                                        true ? (
                                            <span>
                                                {t("Status")}: {t("Archived")}
                                            </span>
                                        ) : (
                                            <span>
                                                {t("Status")}: {t("Unarchived")}
                                            </span>
                                        )
                                    ) : e.selectedRow.actions[0]?.item ===
                                      "isActive" ? (
                                        e.selectedRow.actions[0]?.oldValue ===
                                        true ? (
                                            <span>
                                                {t("State")}: {t("Activated")}
                                            </span>
                                        ) : (
                                            <span>
                                                {t("State")}: {t("Deactivated")}
                                            </span>
                                        )
                                    ) : (
                                        <span>
                                            {e.selectedRow.actions
                                                .map(
                                                    (el: {
                                                        item: string;
                                                        oldValue?: unknown;
                                                        newValue?: unknown;
                                                    }) =>
                                                        el.item !==
                                                        "sortedVisibility"
                                                            ? el.item ===
                                                              "isCentralizationActive"
                                                                ? el.oldValue ===
                                                                  true
                                                                    ? t(
                                                                          "Activation"
                                                                      )
                                                                    : t(
                                                                          "Deactivation"
                                                                      )
                                                                : el.oldValue
                                                            : ""
                                                )
                                                .join(",")}
                                        </span>
                                    )}
                                </span>
                            );
                        }
                    } else if (e.selectedRow.operation === "creation") {
                        return null;
                    } else if (e.selectedRow.operation === "Affectation") {
                        return <span>{""}</span>;
                    } else {
                        return <span>{""}</span>;
                    }
                },
            },
            {
                indexOFColumn: 4,
                columnName: t("After edit"),
                customJsx: (e: { selectedRow: FidelityRow }) => {
                    if (e.selectedRow.operation === "modify") {
                        if (
                            e.selectedRow?.actions[0].item ===
                            "sortedVisibility"
                        ) {
                            return <span>{""}</span>;
                        } else {
                            return (
                                <span>
                                    {e.selectedRow.actions[0].item ===
                                    "isArchived" ? (
                                        e.selectedRow.actions[0].newValue ===
                                        true ? (
                                            <span>
                                                {t("Status")}: {t("Archived")}
                                            </span>
                                        ) : (
                                            <span>
                                                {t("Status")}: {t("Unarchived")}
                                            </span>
                                        )
                                    ) : e.selectedRow.actions[0].item ===
                                      "isActive" ? (
                                        e.selectedRow.actions[0].newValue ===
                                        true ? (
                                            <span>
                                                {t("State")}: {t("Activated")}
                                            </span>
                                        ) : (
                                            <span>
                                                {t("State")}: {t("Deactivated")}
                                            </span>
                                        )
                                    ) : (
                                        <span>
                                            {e.selectedRow.actions
                                                .map(
                                                    (el: {
                                                        item: string;
                                                        oldValue?: unknown;
                                                        newValue?: unknown;
                                                    }) =>
                                                        el.item !==
                                                        "sortedVisibility"
                                                            ? el.item ===
                                                              "isCentralizationActive"
                                                                ? el.newValue ===
                                                                  true
                                                                    ? t(
                                                                          "Activation"
                                                                      )
                                                                    : t(
                                                                          "Deactivation"
                                                                      )
                                                                : el.newValue
                                                            : ""
                                                )
                                                .join(",")}
                                        </span>
                                    )}
                                </span>
                            );
                        }
                    } else if (e.selectedRow.operation === "creation") {
                        return e.selectedRow[t("Designation")] !== undefined
                            ? e.selectedRow[t("Designation")]
                            : "--";
                    } else if (e.selectedRow.operation === "Affectation") {
                        return <span>{""}</span>;
                    } else {
                        return <span>{""}</span>;
                    }
                },
            },
        ],
        // Why: only `t` matters for the column labels. The selected-row data is
        // read at render time via the cell renderer closure — re-memoizing on
        // every render would defeat the table virtualization downstream.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t]
    );
    React.useEffect(() => {
        setArrayIds(selectedFidelity.map((el) => el.Id));
    }, [selectedFidelity]);

    return (
        <React.Fragment>
            <Modal
                centered
                backdrop="static"
                fade={false}
                scrollable
                className="modal-wrapper__clz"
                isOpen={isModalOpened}
            >
                <ModalHeader
                    toggle={() => {
                        setIsModalOpened!(!isModalOpened);
                        setDataUpdated(true);
                        resetStore();
                    }}
                >
                    <StyledH2 className="fed_txt_HStitleHistory">
                        {capitalize(t("Historical"))}
                    </StyledH2>
                </ModalHeader>

                <ModalBody>
                    <div className="d-flex title__clz">
                        <div
                            className={`${classnames(
                                "title__clz fed_txt_HSname",
                                {
                                    "not-allowed-icon__clz":
                                        selectedFidelity.length > 1,
                                }
                            )}`}
                        >
                            {t("Display name")} :
                            <span>
                                {selectedFidelity.length > 1
                                    ? null
                                    : data?.data !== undefined
                                    ? data?.data![0][t("Display name")]
                                    : ""}
                            </span>
                        </div>
                        <div
                            style={{ marginLeft: "200px" }}
                            className={`${classnames(
                                "title__clz fed_txt_HStype",
                                {
                                    "not-allowed-icon__clz":
                                        selectedFidelity.length > 1,
                                }
                            )}`}
                        >
                            {t("Type")} :
                            <span>
                                {selectedFidelity.length > 1
                                    ? null
                                    : data?.data !== undefined
                                    ? data?.data![0][t("Loyalty")]
                                    : ""}
                            </span>
                        </div>
                    </div>
                    <DynamicTable
                        url={url}
                        requestHeader={{
                            "Accept-Language": i18nextLng,
                            ...getAuthHeader,
                        }}
                        canResize
                        actionColumn={ActionColumn}
                        showGlobalFilter
                        showFilter
                        setLocalFilterActive={setLocalFilterActive}
                        filterActive={filterActive}
                        setDataIsUpdated={setDataIsUpdated}
                        dataIsUpdated={dataIsUpdated}
                        elevationTable={1}
                        minHeight="500px"
                        maxHeight="500px"
                        name="historyFidelity"
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        setData={setData}
                    />
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <CustomMainColorButton
                        variant="primary"
                        className="fed_btn_HSclose"
                        onClick={() => {
                            setIsModalOpened(!isModalOpened);
                            setDataUpdated(true);
                        }}
                        rounded
                    >
                        {t("Close")}
                    </CustomMainColorButton>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
}
